import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart, Line, AreaChart, Area, ComposedChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { apiUrl, type UnitScale, unitLabel } from '../App';
import { authedFetch } from '../supabaseClient';
import KpiTooltip from '../KpiTooltip';

// ── Types ─────────────────────────────────────────────────────────────────────

interface StatsRow {
  offset: number;
  min: number | null;
  p25: number | null;
  median: number | null;
  mean: number | null;
  p75: number | null;
  max: number | null;
  count: number;
}

interface CohortData {
  cohort_list: string[];
  max_offsets: number;
  period_cols: string[];
  cohort_revenue_matrix: (number | null)[][];
  cohort_active_matrix: (number | null)[][];
  revenue_retention: (number | null)[][];
  logo_retention: (number | null)[][];
  cumulative_rev_per_cust: (number | null)[][];
  retention_stats: {
    revenue_retention: StatsRow[];
    logo_retention: StatsRow[];
    cumulative_rev_per_cust: StatsRow[];
  };
  layer_cake: Record<string, Record<string, number>>;
  acquired_per_period: Record<string, number>;
}

interface CohortAnalyticsData {
  period_cols: string[];
  period_type: string;
  filter_options: {
    segments: string[];
    revenue_types: string[];
    products: string[];
  };
  cohort: CohortData;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatPeriod(period: string): string {
  const [year, month] = period.split('-');
  return `${parseInt(month)}/${year}`;
}

let _scale = 1000;

function formatVal(val: number): string {
  if (val === 0) return '';
  const abs = Math.abs(val) / _scale;
  const str = abs < 1 ? abs.toFixed(1) : Math.round(abs).toLocaleString();
  return val < 0 ? `(${str})` : str;
}

function formatPctInt(val: number | null | undefined): string {
  if (val === null || val === undefined) return '';
  return Math.round(val) + '%';
}

function formatDollarRaw(val: number | null | undefined): string {
  if (val === null || val === undefined) return '';
  return Math.round(val).toLocaleString();
}

// Generate a color palette — darker for older cohorts, lighter for newer
function cohortColors(count: number): string[] {
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    const t = count <= 1 ? 0.5 : i / (count - 1);
    const h = 210 + t * 30;       // blue range 210-240
    const s = 60 - t * 20;         // saturation 60% → 40%
    const l = 35 + t * 35;         // lightness 35% → 70%
    colors.push(`hsl(${h}, ${s}%, ${l}%)`);
  }
  return colors;
}

// ── Filter Bar ───────────────────────────────────────────────────────────────

function FilterBar({
  filterOptions,
  filters,
  onFiltersChange,
}: {
  filterOptions: CohortAnalyticsData['filter_options'];
  filters: Record<string, string>;
  onFiltersChange: (f: Record<string, string>) => void;
}) {
  const update = (key: string, value: string) => {
    const next = { ...filters };
    if (value === '') delete next[key];
    else next[key] = value;
    onFiltersChange(next);
  };

  const dropdowns = [
    { label: 'Segment',      key: 'segment',      options: filterOptions.segments },
    { label: 'Revenue Type', key: 'revenue_type',  options: filterOptions.revenue_types },
    { label: 'Product',      key: 'product',       options: filterOptions.products },
  ];

  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4 mb-4">
      <span className="text-gray-500 text-xs font-medium">{unitLabel(_scale as UnitScale)}</span>
      {dropdowns.map(d => (
        <div key={d.key} className="flex items-center gap-2">
          <label className="text-gray-500 text-xs font-medium">{d.label}</label>
          <select
            value={filters[d.key] || ''}
            onChange={e => update(d.key, e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-blue-500 min-w-[120px] flex-1 md:flex-none"
          >
            <option value="">All</option>
            {d.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      ))}
      {Object.keys(filters).length > 0 && (
        <button onClick={() => onFiltersChange({})} className="text-gray-500 hover:text-white text-xs transition-colors ml-1">
          Clear filters
        </button>
      )}
    </div>
  );
}

// ── Cohort Matrix Table ──────────────────────────────────────────────────────

function CohortMatrix({
  title,
  cohortList,
  periodCols,
  acquiredPerPeriod,
  matrix,
  stats,
  formatCell,
  maxOffsets,
  kpiKey,
  projectId,
  periodType,
}: {
  title: string;
  cohortList: string[];
  periodCols: string[];
  acquiredPerPeriod: Record<string, number>;
  matrix: (number | null)[][];
  stats: StatsRow[];
  formatCell: (v: number | null) => string;
  maxOffsets: number;
  kpiKey?: string;
  projectId?: string;
  periodType?: string;
}) {
  const [collapsed, setCollapsed] = useState(false);

  // Build a lookup from cohort date to matrix row index
  const cohortIndex = new Map(cohortList.map((c, i) => [c, i]));

  const offsets = Array.from({ length: maxOffsets }, (_, i) => i);
  const offsetPrefix = periodType === 'quarterly' ? 'Q' : 'M';
  const COL_COHORT = 80;
  const COL_ACQ = 56;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg">
      <div
        className="flex items-center gap-2 px-3 md:px-5 py-3 cursor-pointer hover:bg-[#192230] border-b border-gray-700"
        onClick={() => setCollapsed(!collapsed)}
      >
        <span className="text-gray-500 text-xs select-none w-3">{collapsed ? '▶' : '▼'}</span>
        <span className="font-bold text-white text-[11px] md:text-sm">{title}</span>
      </div>
      {!collapsed && (
        <div className="overflow-auto max-h-[60vh]">
          <table className="w-full text-[11px] md:text-xs">
            <thead className="sticky top-0 z-20">
              <tr className="border-b border-gray-700 bg-gray-900">
                <th className="sticky left-0 z-30 bg-gray-900 text-left px-3 py-2 text-gray-400 font-medium"
                    style={{ width: COL_COHORT, minWidth: COL_COHORT }}>Cohort</th>
                <th className="sticky z-30 bg-gray-900 text-right px-2 py-2 text-gray-400 font-medium"
                    style={{ left: COL_COHORT, width: COL_ACQ, minWidth: COL_ACQ }}># Cust</th>
                {offsets.map(i => (
                  <th key={i} className="bg-gray-900 text-right px-3 py-2 font-bold text-gray-200 whitespace-nowrap">
                    {offsetPrefix}{i + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {periodCols.map(period => {
                const idx = cohortIndex.get(period);
                const hasCohort = idx !== undefined;
                return (
                  <tr key={period} className="border-b border-gray-800/40 hover:bg-gray-800/30">
                    <td className="sticky left-0 z-10 bg-gray-900 px-3 py-1.5 text-gray-400 whitespace-nowrap"
                        style={{ width: COL_COHORT, minWidth: COL_COHORT }}>
                      {formatPeriod(period)}
                    </td>
                    <td className="sticky z-10 bg-gray-900 text-right px-2 py-1.5 text-gray-500"
                        style={{ left: COL_COHORT }}>
                      {hasCohort ? (acquiredPerPeriod[period] ?? '') : '-'}
                    </td>
                    {hasCohort
                      ? offsets.map(i => {
                          const cellText = formatCell(matrix[idx]?.[i] ?? null);
                          return (
                            <td key={i} className="text-right px-3 py-1.5 text-gray-300 whitespace-nowrap">
                              {kpiKey && projectId
                                ? <KpiTooltip kpiKey={kpiKey} projectId={projectId}>{cellText}</KpiTooltip>
                                : cellText}
                            </td>
                          );
                        })
                      : offsets.map(i => (
                          <td key={i} className="text-right px-3 py-1.5 text-gray-600 whitespace-nowrap" />
                        ))
                    }
                  </tr>
                );
              })}

              {/* Spacer row */}
              <tr className="h-2" />

              {/* Percentile stats */}
              {['min', 'p25', 'median', 'mean', 'p75', 'max'].map(stat => (
                <tr key={stat} className="border-b border-gray-800/40">
                  <td className="sticky left-0 z-10 bg-gray-900 px-3 py-1.5 text-gray-500 font-medium whitespace-nowrap"
                      style={{ width: COL_COHORT, minWidth: COL_COHORT }}>
                    {stat === 'p25' ? '25th Pctl' : stat === 'p75' ? '75th Pctl' :
                     stat.charAt(0).toUpperCase() + stat.slice(1)}
                  </td>
                  <td className="sticky z-10 bg-gray-900 px-2 py-1.5" style={{ left: COL_COHORT }} />
                  {offsets.map(i => {
                    const s = stats[i];
                    const v = s ? (s as any)[stat] : null;
                    return (
                      <td key={i} className="text-right px-3 py-1.5 text-gray-400 whitespace-nowrap">
                        {formatCell(v)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Retention Line Chart ─────────────────────────────────────────────────────

function RetentionChart({
  title,
  cohortList,
  matrix,
  maxOffsets,
  yAxisFormat,
  yDomain,
  periodType,
}: {
  title: string;
  cohortList: string[];
  matrix: (number | null)[][];
  maxOffsets: number;
  yAxisFormat: (v: number) => string;
  yDomain?: [number | string, number | string];
  periodType?: string;
}) {
  const colors = useMemo(() => cohortColors(cohortList.length), [cohortList.length]);
  const offsetPrefix = periodType === 'quarterly' ? 'Q' : 'M';

  const chartData = useMemo(() => {
    const data: Record<string, any>[] = [];
    for (let offset = 0; offset < maxOffsets; offset++) {
      const point: Record<string, any> = { name: `${offsetPrefix}${offset + 1}` };
      cohortList.forEach((cohort, idx) => {
        const v = matrix[idx]?.[offset];
        if (v != null) point[cohort] = v;
      });
      data.push(point);
    }
    return data;
  }, [cohortList, matrix, maxOffsets]);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-bold text-white mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis tickFormatter={yAxisFormat} tick={{ fill: '#6b7280', fontSize: 10 }} domain={yDomain} />
          <Tooltip
            contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 11 }}
            labelStyle={{ color: '#9ca3af' }}
            formatter={(value: any, name: any) => [yAxisFormat(Number(value)), formatPeriod(String(name))]}
          />
          <Legend
            wrapperStyle={{ fontSize: 10 }}
            formatter={(value: any) => formatPeriod(String(value))}
          />
          {cohortList.map((cohort, idx) => (
            <Line
              key={cohort}
              type="monotone"
              dataKey={cohort}
              stroke={colors[idx]}
              strokeWidth={1.5}
              dot={false}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Layer Cake Chart ─────────────────────────────────────────────────────────

function LayerCakeChart({
  cohortList,
  layerCake,
  periodCols,
}: {
  cohortList: string[];
  layerCake: Record<string, Record<string, number>>;
  periodCols: string[];
}) {
  const colors = useMemo(() => cohortColors(cohortList.length), [cohortList.length]);

  const chartData = useMemo(() => {
    return periodCols.map(p => {
      const point: Record<string, any> = { name: formatPeriod(p) };
      cohortList.forEach(cohort => {
        point[cohort] = (layerCake[cohort]?.[p] ?? 0) / _scale;
      });
      return point;
    });
  }, [cohortList, layerCake, periodCols, _scale]);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-bold text-white mb-4">Revenue by Cohort (Layer Cake)</h3>
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis tickFormatter={(v: number) => `$${v.toLocaleString()}`} tick={{ fill: '#6b7280', fontSize: 10 }} />
          <Tooltip
            contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 11 }}
            labelStyle={{ color: '#9ca3af' }}
            formatter={(value: any, name: any) => [`$${Number(value).toLocaleString()}`, formatPeriod(String(name))]}
          />
          <Legend
            wrapperStyle={{ fontSize: 10 }}
            formatter={(value: any) => formatPeriod(String(value))}
          />
          {/* Oldest cohort at bottom */}
          {cohortList.map((cohort, idx) => (
            <Area
              key={cohort}
              type="monotone"
              dataKey={cohort}
              stackId="1"
              stroke={colors[idx]}
              fill={colors[idx]}
              fillOpacity={0.8}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Milestone Chart (Customers Acquired or Cumulative Rev at T+n) ────────────

function MilestoneChart({
  title,
  data,
  yFormat,
}: {
  title: string;
  data: { name: string; value: number | null }[];
  yFormat: (v: number) => string;
}) {
  // Filter to non-null entries for trendline
  const validData = data.filter(d => d.value != null);
  const validValues = validData.map(d => d.value!);

  // Simple linear trendline
  const trendData = useMemo(() => {
    if (validValues.length < 2) return data.map(d => ({ ...d, trend: null as number | null }));
    const n = validValues.length;
    const sumX = validValues.reduce((s, _, i) => s + i, 0);
    const sumY = validValues.reduce((s, v) => s + v, 0);
    const sumXY = validValues.reduce((s, v, i) => s + i * v, 0);
    const sumXX = validValues.reduce((s, _, i) => s + i * i, 0);
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    let trendIdx = 0;
    return data.map(d => {
      if (d.value != null) {
        const trend = intercept + slope * trendIdx;
        trendIdx++;
        return { ...d, trend };
      }
      return { ...d, trend: null };
    });
  }, [data, validValues]);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-bold text-white mb-3">{title}</h3>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={trendData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 9 }} interval="preserveStartEnd" />
          <YAxis tickFormatter={yFormat} tick={{ fill: '#6b7280', fontSize: 9 }} />
          <Tooltip
            contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 11 }}
            labelStyle={{ color: '#9ca3af' }}
            formatter={(value: any, name: any) => [yFormat(Number(value)), String(name) === 'trend' ? 'Trend' : 'Value']}
          />
          <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} connectNulls={false} />
          <Line type="monotone" dataKey="trend" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 4" dot={false} connectNulls={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Cohort Cross-Section Chart ───────────────────────────────────────────────

function fmtPctInt(v: number): string { return `${Math.round(v)}%`; }
function fmtDollarK(v: number): string {
  const abs = Math.abs(v) / _scale;
  const str = abs < 1 ? abs.toFixed(1) : Math.round(abs).toLocaleString();
  return v < 0 ? `($${str})` : `$${str}`;
}

function CohortCrossSection({ title, subtitle, cohortList, matrix, offsetIdx, format }: {
  title: string; subtitle: string; cohortList: string[];
  matrix: (number | null)[][]; offsetIdx: number; format: 'pct' | 'dollar';
}) {
  const fmt = format === 'pct' ? fmtPctInt : fmtDollarK;
  const chartData = useMemo(() =>
    cohortList.map((c, idx) => ({ name: formatPeriod(c), value: matrix[idx]?.[offsetIdx] ?? null })),
    [cohortList, matrix, offsetIdx]
  );
  if (!chartData.some(d => d.value != null)) return null;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-2">
      <div className="mb-1">
        <p className="text-xs font-bold text-gray-400">{title}</p>
        <p className="text-gray-600 text-[10px]">{subtitle}</p>
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
          <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 7 }} interval="preserveStartEnd" tickLine={false} angle={-45} textAnchor="end" height={30} />
          <YAxis tickFormatter={(v: any) => fmt(Number(v))} tick={{ fill: '#6b7280', fontSize: 7 }} width={44} />
          <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 6, fontSize: 10 }}
            formatter={(value: any) => [fmt(Number(value)), '']} labelFormatter={(l: any) => String(l)} />
          <Bar dataKey="value" fill="#3b82f6" radius={[1, 1, 0, 0]} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Auto-derive T+n milestones ───────────────────────────────────────────────

function deriveMilestones(maxOffsets: number, cumRevMatrix: (number | null)[][]): number[] {
  // Candidate milestones in order of priority
  const candidates = [1, 6, 13, 25, 37];
  const milestones: number[] = [];

  for (const t of candidates) {
    if (t >= maxOffsets) continue;
    // Count cohorts that have data at this offset
    const count = cumRevMatrix.filter(row => row[t] != null).length;
    if (count >= 3) milestones.push(t);
  }

  // If we got fewer than 2 milestones, try filling with whatever has data
  if (milestones.length < 2) {
    for (let t = 1; t < maxOffsets && milestones.length < 4; t++) {
      if (milestones.includes(t)) continue;
      const count = cumRevMatrix.filter(row => row[t] != null).length;
      if (count >= 2) milestones.push(t);
    }
    milestones.sort((a, b) => a - b);
  }

  return milestones;
}

// ── Main View ────────────────────────────────────────────────────────────────

export default function CohortAnalysisView({
  projectId,
  filters,
  onFiltersChange,
  unitScale,
}: {
  projectId: string;
  filters: Record<string, string>;
  onFiltersChange: (f: Record<string, string>) => void;
  unitScale: UnitScale;
}) {
  _scale = unitScale;
  const [data, setData]       = useState<CohortAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const extra: Record<string, string> = {};
    if (filters.segment)      extra.segment = filters.segment;
    if (filters.revenue_type) extra.revenue_type = filters.revenue_type;
    if (filters.product)      extra.product = filters.product;

    authedFetch(apiUrl('/api/customers/analytics', projectId, extra))
      .then(res => {
        if (!res.ok) throw new Error(res.status === 404 ? 'No customer data' : 'Fetch failed');
        return res.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [projectId, filters]);

  // Derived milestone offsets
  const milestones = useMemo(() => {
    if (!data) return [];
    return deriveMilestones(data.cohort.max_offsets, data.cohort.cumulative_rev_per_cust);
  }, [data]);

  const validOffsets = useMemo(() => {
    if (!data) return [];
    const offsets: number[] = [];
    const cohortList = data.cohort.cohort_list;
    const revenueRetention = data.cohort.revenue_retention;
    const maxOffsets = data.cohort.max_offsets;

    for (let i = 0; i < maxOffsets; i++) {
      const count = cohortList.filter((_, idx) =>
        revenueRetention[idx]?.[i] != null
      ).length;
      if (count >= 3) offsets.push(i);
    }
    return offsets;
  }, [data]);

  const periodLabel = data?.period_type === 'quarterly' ? 'Quarter' : 'Month';

  if (loading) return <p className="text-gray-500 text-sm">Loading...</p>;
  if (error) return (
    <div className="text-center py-16">
      <p className="text-gray-500 text-sm">{error === 'No customer data' ? 'No customer revenue data uploaded for this project.' : error}</p>
    </div>
  );
  if (!data) return null;

  const { cohort } = data;
  const { cohort_list, max_offsets, revenue_retention, logo_retention,
          cumulative_rev_per_cust, retention_stats, layer_cake,
          acquired_per_period } = cohort;

  // Skip first cohort for bottom charts (first period is a catch-all of pre-existing customers)
  const chartCohortList = cohort_list.slice(1);

  // Build milestone chart data (excluding first cohort)
  const milestoneChartData = (offsetIdx: number) =>
    chartCohortList.map(c => ({
      name: formatPeriod(c),
      value: cumulative_rev_per_cust[cohort_list.indexOf(c)]?.[offsetIdx] ?? null,
    }));

  // Customers acquired chart data (excluding first cohort)
  const acquiredData = chartCohortList.map(c => ({
    name: formatPeriod(c),
    value: acquired_per_period[c] ?? 0,
  }));

  // Cross-section offsets: only show M+n where ≥3 cohorts have data

  return (
    <div className="space-y-4">
      <FilterBar
        filterOptions={data.filter_options}
        filters={filters}
        onFiltersChange={onFiltersChange}
      />

      {/* ── Cohort Matrices ── */}
      <CohortMatrix
        title="Revenue Retention"
        cohortList={cohort_list}
        periodCols={data.period_cols}
        acquiredPerPeriod={acquired_per_period}
        matrix={revenue_retention}
        stats={retention_stats.revenue_retention}
        formatCell={formatPctInt}
        maxOffsets={max_offsets}
        kpiKey="revenue_retention_pct"
        projectId={projectId}
        periodType={data.period_type}
      />

      <CohortMatrix
        title="Logo Retention"
        cohortList={cohort_list}
        periodCols={data.period_cols}
        acquiredPerPeriod={acquired_per_period}
        matrix={logo_retention}
        stats={retention_stats.logo_retention}
        formatCell={formatPctInt}
        maxOffsets={max_offsets}
        kpiKey="logo_retention_pct"
        projectId={projectId}
        periodType={data.period_type}
      />

      <CohortMatrix
        title="Cumulative Revenue Per Customer"
        cohortList={cohort_list}
        periodCols={data.period_cols}
        acquiredPerPeriod={acquired_per_period}
        matrix={cumulative_rev_per_cust}
        stats={retention_stats.cumulative_rev_per_cust}
        formatCell={(v) => v != null ? formatDollarRaw(v / _scale) : ''}
        maxOffsets={max_offsets}
        kpiKey="cumulative_rev_per_customer"
        projectId={projectId}
        periodType={data.period_type}
      />

      {/* ── Retention Charts ── */}
      <RetentionChart
        title="Revenue Retention"
        cohortList={cohort_list}
        matrix={revenue_retention}
        maxOffsets={max_offsets}
        yAxisFormat={(v) => `${Math.round(v)}%`}
        periodType={data.period_type}
      />

      <RetentionChart
        title="Logo Retention"
        cohortList={cohort_list}
        matrix={logo_retention}
        maxOffsets={max_offsets}
        yAxisFormat={(v) => `${Math.round(v)}%`}
        yDomain={[0, 100]}
        periodType={data.period_type}
      />

      <RetentionChart
        title="Cumulative Revenue Per Customer"
        cohortList={cohort_list}
        matrix={cumulative_rev_per_cust.map(row => row.map(v => v != null ? v / _scale : null))}
        maxOffsets={max_offsets}
        yAxisFormat={(v) => `$${Math.round(v).toLocaleString()}`}
        periodType={data.period_type}
      />

      {/* ── Layer Cake ── */}
      <LayerCakeChart
        cohortList={cohort_list}
        layerCake={layer_cake}
        periodCols={data.period_cols}
      />

      {/* ── Acquisition & Milestone Charts ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MilestoneChart
          title="Customers Acquired Per Month"
          data={acquiredData}
          yFormat={(v) => v.toLocaleString()}
        />

        {milestones.map(t => (
          <MilestoneChart
            key={t}
            title={`Cumulative Revenue T+${t}`}
            data={milestoneChartData(t).map(d => ({
              ...d,
              value: d.value != null ? d.value / _scale : null,
            }))}
            yFormat={(v) => `$${Math.round(v).toLocaleString()}`}
          />
        ))}
      </div>

      {/* ── Cohort Cross-Sections ── */}
      {validOffsets.length > 0 && (<>
        <div>
          <h3 className="text-sm font-bold text-white mb-3">Revenue Retention by Period</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {validOffsets.map(i => (
              <CohortCrossSection key={`rr-${i}`} title={`${periodLabel} ${i + 1}`}
                subtitle="" cohortList={chartCohortList} matrix={revenue_retention.slice(1)} offsetIdx={i} format="pct" />
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-bold text-white mb-3">Logo Retention by Period</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {validOffsets.map(i => (
              <CohortCrossSection key={`lr-${i}`} title={`${periodLabel} ${i + 1}`}
                subtitle="" cohortList={chartCohortList} matrix={logo_retention.slice(1)} offsetIdx={i} format="pct" />
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-bold text-white mb-3">Cumulative Revenue per Customer by Period</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {validOffsets.map(i => (
              <CohortCrossSection key={`cr-${i}`} title={`${periodLabel} ${i + 1}`}
                subtitle="" cohortList={chartCohortList}
                matrix={cumulative_rev_per_cust.slice(1).map(row => row.map(v => v != null ? v / _scale : null))}
                offsetIdx={i} format="dollar" />
            ))}
          </div>
        </div>
      </>)}
    </div>
  );
}
